/**
 * useSubunitRendering — Bridges subunit data to 3D rendering and collision
 *
 * Fetches subunits for a location, converts them to SubunitRenderData grouped
 * by building, and pushes updates to MapboxCustomLayerService.
 * Subunit collision geometry is merged into the obstacle pipeline.
 */

import { useEffect, useMemo, useRef } from 'react'
import type { MapboxCustomLayerService } from '@/services/3d/MapboxCustomLayerService'
import type { SubunitRenderData } from '@/services/3d/rendering/SubunitRenderer'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'
import { useSubunitsByLocation } from '@/hooks/useSubunits'
import type { Subunit } from '@/types/subunit'
import { FEET_TO_METERS } from '@/constants/units'
import type { SerializedObstacleGeometry } from '@/services/collision/worker/collision.worker.types'
import { getCollisionWorkerService } from '@/services/collision/worker/CollisionWorkerService'

/** Building position + rotation info needed for subunit world-space transform */
interface BuildingTransform {
  position: [number, number, number]
  rotation: number
}

interface UseSubunitRenderingParams {
  locationId: string | undefined
  buildings: Building3DConfigExtended[]
  layerService: MapboxCustomLayerService | null
  collisionEnabled?: boolean
  syncTrigger?: number
}

/**
 * Groups subunits by building ID (via parentUnit → building lookup),
 * converts to SubunitRenderData, and pushes to 3D renderers.
 */
export function useSubunitRendering({
  locationId,
  buildings,
  layerService,
  collisionEnabled = false,
  syncTrigger,
}: UseSubunitRenderingParams) {
  const { data: subunits = [] } = useSubunitsByLocation(locationId)

  // Build parentUnit→building lookup and parentUnit→offset lookup
  const { parentUnitToBuildingMap, parentUnitOffsetMap, buildingTransformMap } = useMemo(() => {
    const u2b = new Map<string, string>()
    const u2o = new Map<string, [number, number]>()
    const b2t = new Map<string, BuildingTransform>()

    for (const building of buildings) {
      if (!building.generatedUnits) continue
      const centerX = building.width / 2
      const tpl = building.templateType as string | undefined
      const centerZ = (tpl === 'double_row' || tpl === 'nested_t_hangar') ? building.depth / 2 : 0

      b2t.set(building.id, {
        position: building.position,
        rotation: building.rotation ?? 0,
      })

      for (const unit of building.generatedUnits) {
        if (!unit.id) continue
        u2b.set(unit.id, building.id)
        u2o.set(unit.id, [unit.offset_x - centerX, unit.offset_y - centerZ])
      }
    }
    return { parentUnitToBuildingMap: u2b, parentUnitOffsetMap: u2o, buildingTransformMap: b2t }
  }, [buildings])

  // Group subunits by building and compute render data
  const renderDataByBuilding = useMemo(() => {
    if (subunits.length === 0 || parentUnitToBuildingMap.size === 0) return new Map<string, SubunitRenderData[]>()

    const map = new Map<string, Map<string, SubunitRenderData>>()

    for (const subunit of subunits) {
      const buildingId = parentUnitToBuildingMap.get(subunit.parentUnitId)
      if (!buildingId) continue

      const offset = parentUnitOffsetMap.get(subunit.parentUnitId) ?? [0, 0]

      if (!map.has(buildingId)) {
        map.set(buildingId, new Map())
      }
      const byParent = map.get(buildingId)!

      if (!byParent.has(subunit.parentUnitId)) {
        byParent.set(subunit.parentUnitId, { subunits: [], parentUnitOffset: offset })
      }
      byParent.get(subunit.parentUnitId)!.subunits.push(subunit)
    }

    const result = new Map<string, SubunitRenderData[]>()
    for (const [buildingId, byParent] of map) {
      result.set(buildingId, Array.from(byParent.values()))
    }
    return result
  }, [subunits, parentUnitToBuildingMap, parentUnitOffsetMap])

  // Push subunit render data to 3D layer
  const prevKeysRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!layerService) return

    const currentKeys = new Set(renderDataByBuilding.keys())

    for (const [buildingId, renderData] of renderDataByBuilding) {
      layerService.updateBuildingSubunits(buildingId, renderData)
    }

    for (const prevKey of prevKeysRef.current) {
      if (!currentKeys.has(prevKey)) {
        layerService.removeBuildingSubunits(prevKey)
      }
    }

    prevKeysRef.current = currentKeys
  }, [renderDataByBuilding, layerService, syncTrigger])

  // Send subunit collision geometry to worker (merged into obstacle pipeline)
  useEffect(() => {
    if (!collisionEnabled || subunits.length === 0 || parentUnitToBuildingMap.size === 0) return

    const serialized = serializeSubunitsForWorker(subunits, parentUnitOffsetMap, parentUnitToBuildingMap, buildingTransformMap)
    if (serialized.meshes.length === 0) return

    try {
      const workerService = getCollisionWorkerService()
      // Merge with existing obstacle geometry by sending as obstacle update
      // The worker's BVH treats all box geometry identically
      workerService.updateObstacleGeometry(serialized)
    } catch (e) {
      // Worker may not be initialized
    }
  }, [subunits, collisionEnabled, parentUnitToBuildingMap, parentUnitOffsetMap, buildingTransformMap])

  return {
    subunits,
    renderDataByBuilding,
  }
}

/**
 * Serialize subunits into flat geometry for the collision worker.
 * Each subunit becomes a box mesh with pre-baked transforms.
 */
function serializeSubunitsForWorker(
  subunits: Subunit[],
  parentUnitOffsetMap: Map<string, [number, number]>,
  parentUnitToBuildingMap: Map<string, string>,
  buildingTransformMap: Map<string, BuildingTransform>,
): SerializedObstacleGeometry {
  const meshes: SerializedObstacleGeometry['meshes'] = []
  const obstacleInfo: SerializedObstacleGeometry['obstacleInfo'] = []

  for (const sub of subunits) {
    const buildingId = parentUnitToBuildingMap.get(sub.parentUnitId)
    if (!buildingId) continue

    const offset = parentUnitOffsetMap.get(sub.parentUnitId) ?? [0, 0]
    const buildingTransform = buildingTransformMap.get(buildingId)

    // Step 1-2: Subunit position in building-local space (centered, feet)
    const centeredX = offset[0] + sub.offsetX
    const centeredZ = -(offset[1] + sub.offsetY)

    // Step 3: Apply building rotation
    const rotationRad = ((buildingTransform?.rotation ?? 0) * Math.PI) / 180
    const cos = Math.cos(rotationRad)
    const sin = Math.sin(rotationRad)
    const rotatedX = centeredX * cos + centeredZ * sin
    const rotatedZ = -centeredX * sin + centeredZ * cos

    // Step 4: Add building world position (feet) then convert to meters
    const bPos = buildingTransform?.position ?? [0, 0, 0]
    const posX = (bPos[0] + rotatedX) * FEET_TO_METERS
    const posY = sub.elevationFt * FEET_TO_METERS
    const posZ = (bPos[2] + rotatedZ) * FEET_TO_METERS

    // Rectangle box: 8 vertices, 12 triangles (6 faces)
    const hw = (sub.width * FEET_TO_METERS) / 2
    const hd = (sub.depth * FEET_TO_METERS) / 2
    const h = sub.height * FEET_TO_METERS

    // Apply building rotation only (subunits are axis-aligned within the building)
    const totalRotRad = -(buildingTransform?.rotation ?? 0) * Math.PI / 180
    const tcos = Math.cos(totalRotRad)
    const tsin = Math.sin(totalRotRad)

    const corners = [
      [-hw, -hd], [hw, -hd], [hw, hd], [-hw, hd],
    ]
    const positions = new Float32Array(8 * 3)
    for (let i = 0; i < 4; i++) {
      const [lx, lz] = corners[i]
      const rx = lx * tcos - lz * tsin + posX
      const rz = lx * tsin + lz * tcos + posZ
      positions[i * 3] = rx
      positions[i * 3 + 1] = posY
      positions[i * 3 + 2] = rz
      positions[(i + 4) * 3] = rx
      positions[(i + 4) * 3 + 1] = posY + h
      positions[(i + 4) * 3 + 2] = rz
    }

    const indices = new Uint32Array([
      0, 1, 2, 0, 2, 3,
      4, 6, 5, 4, 7, 6,
      0, 1, 5, 0, 5, 4,
      2, 3, 7, 2, 7, 6,
      0, 3, 7, 0, 7, 4,
      1, 2, 6, 1, 6, 5,
    ])

    const worldMatrix = new Float32Array([
      1, 0, 0, 0,
      0, 1, 0, 0,
      0, 0, 1, 0,
      0, 0, 0, 1,
    ])

    meshes.push({ positions, indices, worldMatrix })
    obstacleInfo.push({
      obstacleId: sub.id,
      obstacleName: sub.name,
      unitId: sub.parentUnitId,
      areaId: buildingId,
    })
  }

  return { meshes, obstacleInfo }
}
